import MoreVert from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments,
  RateOwnerType,
  RateType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import RateAndTypeInput from '~components/FormFields/v2/RateAndTypeInput';
import { commissionPercentageSchema } from '~components/PayCommission/commissionPercentageSchema';
import PayCommissionControl from '~components/PayCommission/PayCommissionControl';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { RateBasicWithValue } from '~hooks/useRates/models';
import AddOnAssignmentCard from '~pages/Sales/Orders/NewOrderForm/components/AddOnAssignmentCard';
import {
  AddOnAssignmentSchema,
  AutomaticAddOnsDTO,
  constructDefaultValues,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import AutomaticRateAddOnDialog from '~pages/Sales/Orders/NewOrderForm/components/AutomaticRateAddOnDialog';
import { SelectRateCardDialog } from '~pages/Sales/Projects/components/SelectRateCardDialog';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store';
import {
  isGrossRateUnchanged,
  JobPayCommission,
  resolveRateSubmit,
  savedPayCommissionOverride,
} from '~utils/jobs/job-utils';
import { toRateNumber } from '~utils/rateFormatting';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { JobRateInputHandler } from '../hooks/useJobScheduleRates';

// Which host mounts the job rate widgets — the `surface` property on
// `job_rate_field_edited` events.
export type JobRateAnalyticsSurface = 'edit_rates_dialog' | 'job_edit_form';

interface RateFormValues {
  targetPayCommissionPercentage?: string | null;
  rate: {
    rate: number | null;
    type: { value: RateType; label: string } | null;
  } | null;
}

interface JobRateInputProps {
  commission?: JobPayCommission;
  label: string;
  rateOwnerType: RateOwnerType;
  targetRate: RateBasicWithValue | null;
  targetRateType: RateType | null;
  targetRateValue: number | null;
  fetchRates: (args?: { query: string; cursor: string }) => void;
  // Saved auto-add-ons for this side of the job. Pre-fetched and shaped by
  // `JobFreightRateRow` so the schedule picker can't fire before they're in
  // state — that race let stale add-ons survive an SRS swap (TRE-14276).
  initialAddOns: AddOnAssignmentSchema[];
  // `YYYY-MM-DD`. Fuel surcharge add-ons bill the percentage in effect on the
  // job's date; without it the API falls back to today's and contradicts the
  // invoice.
  jobStartDate?: string;
  onRateFieldsChange?: (values: {
    targetRate: RateBasicWithValue | null;
    targetRateType: RateType | null;
    targetRateValue: number | null;
  }) => void;
  onDirtyStatusChange?: (isDirty: boolean) => void;
  isDisabled?: boolean;
  disabledReason?: string;
  analyticsSurface: JobRateAnalyticsSurface;
}

/**
 * Compact rate input for the Job form: an inline rate value + type selector
 * with a kebab menu for "Use Rate Card" and "Edit Add Ons".
 *
 * Exposes the `JobRateInputHandler` imperative interface (extends
 * `RateDetailsHandler` with the schedule-apply methods) so
 * `JobEditForm`'s submit path and `useJobScheduleRates` both plug in.
 */
const JobRateInput = forwardRef<JobRateInputHandler, JobRateInputProps>(
  function JobRateInput(
    {
      label,
      commission,
      rateOwnerType,
      targetRate,
      targetRateType,
      targetRateValue,
      fetchRates,
      initialAddOns,
      jobStartDate,
      onRateFieldsChange,
      onDirtyStatusChange,
      isDisabled,
      disabledReason,
      analyticsSurface,
    },
    ref,
  ) {
    const { userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const savedCommissionOverride = savedPayCommissionOverride(commission);
    const { control, setValue, setError, clearErrors, watch } = useForm<RateFormValues>({
      defaultValues: {
        targetPayCommissionPercentage: savedCommissionOverride,
        rate: {
          rate: targetRateValue,
          type: targetRateType
            ? { value: targetRateType, label: getRateTypeLabel(targetRateType) }
            : null,
        },
      },
    });

    const [rateCard, setRateCard] = useState<RateBasicWithValue | null>(targetRate);
    const [addOns, setAddOns] = useState<AddOnAssignmentSchema[]>(initialAddOns);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [isRateCardDialogOpen, setIsRateCardDialogOpen] = useState(false);
    const [isAddOnDialogOpen, setIsAddOnDialogOpen] = useState(false);

    // Tracks `job_rate_field_edited` once per field per mount: the question is
    // "did the user touch this side's rate / add-ons on this job", and
    // keystroke-level repeats would only flood PostHog. `scope: 'job'` keeps
    // event shapes uniform with the form-level rates events (which use
    // 'project' / 'order') while staying honest: these edits target a job.
    const trackedFieldEditsRef = useRef(new Set<string>());
    const trackFieldEditOnce = useCallback(
      (field: string) => {
        if (trackedFieldEditsRef.current.has(field)) return;
        trackedFieldEditsRef.current.add(field);
        AnalyticsController.track('job_rate_field_edited', {
          scope: 'job',
          field,
          surface: analyticsSurface,
        });
      },
      [analyticsSurface],
    );

    // `applyRate` is the schedule picker's programmatic write — it must not
    // read as a user edit of this side's rate. The watch subscription below
    // checks this flag synchronously: react-hook-form (7.72, the pinned major)
    // notifies watch subscribers inside the setValue call itself, so setting
    // the flag around applyRate's setValue is sufficient. If a future RHF
    // major batches notifications past the synchronous window, the suppress
    // breaks and every schedule pick would read as a manual rate edit — the
    // "does not track applyRate" test in JobRateInput.test.tsx pins this.
    const isApplyingProgrammaticRateRef = useRef(false);
    useEffect(() => {
      const subscription = watch((_, info) => {
        // Only the rate field family counts — matches the event this sends.
        if (!info?.name?.startsWith('rate')) return;
        if (isApplyingProgrammaticRateRef.current) return;
        trackFieldEditOnce(`${rateOwnerType}_rate`);
      });
      return () => subscription.unsubscribe();
    }, [watch, rateOwnerType, trackFieldEditOnce]);

    // Notify parent of rate field changes for divergence detection.
    //
    // Both callbacks are held in refs: their upstream identities are
    // unstable (`onRateFieldsChange` chains through `makeDivergenceWatcher` ->
    // inline `getSchedule`/`setSchedule` in `JobEditForm`, and
    // `onDirtyStatusChange` is an inline arrow on every render), so listing
    // them as deps would fire this effect on every render and cascade dirty
    // notifications upward.
    const watchedCommission = watch('targetPayCommissionPercentage');
    const watchedRate = watch('rate');
    const previousGrossRate = useRef(
      JSON.stringify([targetRate?.id ?? null, targetRateType, targetRateValue]),
    );
    const rateValue = watchedRate?.rate ?? null;
    const rateTypeValue = watchedRate?.type?.value ?? null;
    const isFirstDirtyReport = useRef(true);
    const onRateFieldsChangeRef = useRef(onRateFieldsChange);
    const onDirtyStatusChangeRef = useRef(onDirtyStatusChange);
    onRateFieldsChangeRef.current = onRateFieldsChange;
    onDirtyStatusChangeRef.current = onDirtyStatusChange;
    useEffect(() => {
      const normalizedRateValue = rateValue !== null ? Number(rateValue) : null;
      const grossRateKey = JSON.stringify([
        rateCard?.id ?? null,
        rateTypeValue,
        normalizedRateValue,
      ]);
      if (previousGrossRate.current !== grossRateKey) {
        previousGrossRate.current = grossRateKey;
        onRateFieldsChangeRef.current?.({
          targetRate: rateCard,
          targetRateType: rateTypeValue,
          targetRateValue: normalizedRateValue,
        });
      }
      // Report the actual dirty state (not a latching `true`) so the parent
      // can correctly detect when the user reverts edits back to the initial
      // values.
      const addOnsDidChange = JSON.stringify(addOns) !== JSON.stringify(initialAddOns);
      const isDirty =
        (rateCard?.id ?? null) !== (targetRate?.id ?? null) ||
        rateTypeValue !== targetRateType ||
        normalizedRateValue !== targetRateValue ||
        addOnsDidChange ||
        watchedCommission !== savedCommissionOverride;
      // Both rate sides share one callback and mount independently, so a
      // late-mounting side reporting `false` would clear the other's dirty flag.
      if (isFirstDirtyReport.current) {
        isFirstDirtyReport.current = false;
      } else {
        onDirtyStatusChangeRef.current?.(isDirty);
      }
    }, [
      watchedCommission,
      savedCommissionOverride,
      rateValue,
      rateTypeValue,
      rateCard,
      targetRate,
      targetRateType,
      targetRateValue,
      addOns,
      initialAddOns,
    ]);

    // When the user clears both the rate value and the rate type, also clear
    // the bound rate-card state. Otherwise the submit DTO emits a stale rate
    // id alongside null type/value and the BE rejects the patch with
    // "Rate type can't be blank" (see TRE-14369). Mirrors the legacy
    // RateDetails.handleClearTargetRate semantics.
    useEffect(() => {
      const isEmptyValue = isNil(rateValue) || String(rateValue).trim() === '';
      const isEmptyType = isNil(rateTypeValue);
      if (isEmptyValue && isEmptyType && rateCard !== null) {
        setRateCard(null);
      }
    }, [rateValue, rateTypeValue, rateCard]);

    // Imperative handle — same interface as RateDetails
    useImperativeHandle(
      ref,
      () => ({
        validateCommission() {
          const valid = commissionPercentageSchema.isValidSync(
            watch('targetPayCommissionPercentage'),
          );
          if (valid) {
            clearErrors('targetPayCommissionPercentage');
          } else {
            setError('targetPayCommissionPercentage', {
              message: t('pay_commission.percentage_invalid'),
            });
          }
          return valid;
        },
        setCommissionError(message) {
          setError('targetPayCommissionPercentage', { message, type: 'server' });
        },
        onSubmit(callback) {
          const currentRate = watch('rate');
          const currentCommission = watch('targetPayCommissionPercentage');
          const submittedRateValue = toRateNumber(currentRate?.rate);
          const grossUnchanged = isGrossRateUnchanged(
            {
              rateId: rateCard?.id,
              rateType: currentRate?.type?.value,
              rateValue: submittedRateValue,
            },
            {
              rateId: targetRate?.id,
              rateType: targetRateType,
              rateValue: targetRateValue,
            },
          );
          const addOnsDidChange =
            JSON.stringify(addOns) !== JSON.stringify(initialAddOns);
          const { nothingChanged, omitGrossRate, payCommissionPercentage } =
            resolveRateSubmit({
              grossUnchanged,
              rateType: currentRate?.type?.value,
              savedPercentage: savedCommissionOverride,
              submittedPercentage: currentCommission,
            });
          if (nothingChanged) {
            callback(undefined, addOns, addOnsDidChange);
            return;
          }

          const submittedRateCard = rateCard
            ? {
                id: rateCard.id,
                name: rateCard.name,
                type: rateCard.type,
                rate: rateCard.rate,
              }
            : null;
          const grossRate = omitGrossRate
            ? {}
            : {
                targetRate: submittedRateCard,
                targetRateType: currentRate?.type ?? null,
                targetRateValue: submittedRateValue,
              };
          callback(
            { targetPayCommissionPercentage: payCommissionPercentage, ...grossRate },
            addOns,
            addOnsDidChange,
          );
        },
        applyRate({ targetRate, targetRateType, targetRateValue }) {
          setRateCard(targetRate);
          isApplyingProgrammaticRateRef.current = true;
          setValue('rate', {
            rate: targetRateValue,
            type: targetRateType
              ? { value: targetRateType, label: getRateTypeLabel(targetRateType) }
              : null,
          });
          isApplyingProgrammaticRateRef.current = false;
        },
        applyAddOns(newAddOns) {
          // Tombstone any previous add-ons before appending the new ones —
          // matches `handleRateCardSelect`'s pattern. If we just replaced the
          // list, the form would submit without deletion markers for the old
          // items and the BE would keep them, surfacing as duplicates after a
          // reload.
          setAddOns((prev) => [
            ...prev.map((a) => ({ ...a, isDeleted: true })),
            ...newAddOns,
          ]);
        },
      }),
      [
        rateCard,
        addOns,
        initialAddOns,
        watch,
        setValue,
        savedCommissionOverride,
        targetRate,
        targetRateType,
        targetRateValue,
        setError,
        clearErrors,
      ],
    );

    const handleRateCardSelect = useCallback(
      async (rate: RateBasicWithValue | null) => {
        if (!rate) return;
        setRateCard(rate);
        setValue('rate', {
          rate: rate.rate,
          type: {
            value: rate.type as RateType,
            label: rate.readableTypeValue ?? getRateTypeLabel(rate.type as RateType),
          },
        });

        // Fetch auto-add-ons for the selected rate card. Use a functional
        // state update so rapid consecutive selections don't drop tombstones
        // from a stale closure.
        if (rate.id) {
          const response = await getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
            path: { 'company-id': companyId, 'rate-id': rate.id },
          });
          const fetchedAddOns = response.data.data.map((a) => AddOnAssignment.parse(a));
          // Preserve `originalAutoAddOnAssignmentId` so the JobAssignment's
          // addon copies link back to the Rate's addon row. Without it the BE
          // persists them with `original_auto_add_on_assignment_id = nil`,
          // which makes `set_auto_add_ons!` skip them on future re-matching
          // (TRE-14276).
          const dto = constructDefaultValues(fetchedAddOns, true, true);
          setAddOns((prev) => [
            ...prev.map((a) => ({ ...a, isDeleted: true })),
            ...dto.addOns,
          ]);
        }
      },
      [companyId, setValue],
    );

    const handleAddOnSave = useCallback(
      (dto: AutomaticAddOnsDTO) => {
        setAddOns(dto.addOns);
        setIsAddOnDialogOpen(false);
        trackFieldEditOnce(`${rateOwnerType}_add_ons`);
      },
      [rateOwnerType, trackFieldEditOnce],
    );

    const visibleAddOns = addOns.filter((a) => !a.isDeleted);

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
          <RateAndTypeInput
            control={control}
            name="rate"
            label={label}
            rateOwnerType={rateOwnerType}
            isDisabled={isDisabled}
            disabledReason={disabledReason}
          />
          {/*
            The span carries the tooltip because a disabled button, like a
            disabled input, swallows the hover events a title needs.
          */}
          <Box component="span" title={isDisabled ? disabledReason : undefined}>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ mb: 0.5 }}
              disabled={isDisabled}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {rateOwnerType !== RateOwnerType.CUSTOMER && (
          <PayCommissionControl
            control={control}
            name="targetPayCommissionPercentage"
            grossRate={rateValue}
            rateType={rateTypeValue}
            initialValue={savedCommissionOverride}
            inheritedPercentage={commission?.inheritedPercentage}
            savedNetRate={commission?.netRate}
            savedGrossRate={targetRateValue}
            savedRateType={targetRateType}
            isDisabled={isDisabled || commission?.locked !== false}
          />
        )}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setIsRateCardDialogOpen(true);
            }}
          >
            {t('dispatch.site_rates.use_rate_card')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setIsAddOnDialogOpen(true);
            }}
          >
            {t('approvals.driver_pay.settlements.edit_add_ons')}
          </MenuItem>
        </Menu>

        {visibleAddOns.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <AddOnAssignmentCard
              addOnAssignments={visibleAddOns}
              orderDate={jobStartDate}
            />
          </Box>
        )}

        <SelectRateCardDialog
          open={isRateCardDialogOpen}
          onClose={() => setIsRateCardDialogOpen(false)}
          onSelect={handleRateCardSelect}
          fetchRates={fetchRates}
          rateOwnerType={rateOwnerType}
        />

        <AutomaticRateAddOnDialog
          open={isAddOnDialogOpen}
          onClose={() => setIsAddOnDialogOpen(false)}
          onSave={handleAddOnSave}
          rateOwnerType={rateOwnerType}
          initialAddOns={addOns}
        />
      </Box>
    );
  },
);

export default JobRateInput;
